import React, { useEffect, useState } from 'react';
import { useWorkflow } from 'hooks/config/useWorkflow';
import { Button, Card, Modal } from 'components';
import { Box, Grid, Stack, Typography } from '@mui/material';
import DragAndDrop from '../configuration/AnalysisConfiguration/WorkflowMonitor/DragAndDrop';
import TrainSummaryModal from '../configuration/AnalysisConfiguration/WorkflowMonitor/summaryModal';
import { useSnack } from 'plugins/snack';
import { useLocation, useNavigate } from 'react-router-dom';
import { useParams } from 'react-router-dom';
import WorkFlowConfig from './pickAndPlace/workflowConfig/index';
import { useAddWorkflowConfiguration, useGetAllActivities } from 'api/sdk';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { object, string } from 'yup';

const WorkflowMonitor = () => {
  const [modalOpen, setModalOpen] = React.useState(false);
  const [trainingStarted, setTrainingStarted] = React.useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  const method: any = queryParams.get('method');
  const spt: any = queryParams.get('spatiotemporal');
  const model_id: string | null = queryParams.get('modelId');
  const snack = useSnack();
  const [workflowName, setWorkFlowName] = useState('');
  const {
    rows,
    setRows,
    setActiveId,
    activeId,
    addActivity,
    handleChangeDependency,
    isLoading,
    handleSort,
    handleSave,
    activities,
    id: configId,
    cameras,
    train,
    trainLoading
  } = useWorkflow(method);

  const [validationError, setValidationError] = useState<string>('');
  const [createLoad, setCreateLoad] = useState(false);
  const workflowSchema = object({
    name: string()
      .required('Workflow name is required')
      .min(3, 'Name must be at least 3 characters')
      .max(20, 'Name must not exceed 20 characters')
  });
  const handleInputChange = (value: string) => {
    setWorkFlowName(value);
    try {
      workflowSchema.validateSync({ name: value });
      setValidationError('');
    } catch (err: any) {
      setValidationError(err.message);
    }
  };
  const { flowtype } = useParams();
  const [isSaveModal, setSaveModal] = useState(false);
  const videoListId: any = useSelector(
    (state: RootState) => state?.configuration?.filteredVideo
  );
  const { mutateAsync: addWorkflow } = useAddWorkflowConfiguration();

  const { data: allActivities } = useGetAllActivities(configId || '', {
    pipeline_satus: true || null
  });
  const [annotationCount, setAnnotationCount] = useState(0);
  useEffect(() => {
    let annotation_count = allActivities?.data?.activity_details?.flatMap(
      (data: any) =>
        data?.clips.filter((clips: any) =>
          videoListId.includes(clips?.camera_id)
        )
    ).length;
    if (modalOpen) {
      annotation_count && setAnnotationCount(annotation_count);
    }
  }, [modalOpen]);

  const handleTrain = async (model_name: string, epochs: number) => {
    handleSaveAs(workflowName)
      .then(() => {
        train({
          configId: configId || '',
          data: {
            model_name,
            epochs,
            workflow_config: (rows as any)?.data || [],
            video_ids: videoListId,
            annotation_count: annotationCount,
            workflow_name: workflowName
          }
        })
          .then(() => {
            setTrainingStarted(true);
            snack({
              message: 'Model training started successfully',
              severity: 'success'
            });

            setTimeout(() => {
              navigate('/configuration');
            }, 4000);
          })
          .catch(error => {
            console.log('error', error);
            snack({
              message: error?.response?.data?.detail,
              severity: 'error'
            });
          });
      })
      .catch((error: any) => {
        console.log('error', error);
      });
  };

  const handleSaveAs = async (workflowName: string) => {
    console.log(configId, model_id, rows?.data, 'CREATEPRESSED');
    if (!configId || !model_id || !rows?.data) {
      return;
    }

    setCreateLoad(true);
    const workflowConfig = rows?.data.map((data: any) => {
      return {
        activity_id: data?.activity_id,
        dependency: data?.dependency,
        id: data?.id,
        activity_name: allActivities?.data?.activity_details.filter(
          item => item?.id === data?.activity_id
        )[0]?.activity,
        mandatory: data?.mandatory
      };
    });

    try {
      await addWorkflow({
        configId: configId,
        modelId: model_id,
        data: {
          model_id: model_id,
          workflow_config: workflowConfig,
          workflow_name: workflowName
        }
      });

      snack({
        message: 'Workflow saved successfully',
        severity: 'success'
      });
      navigate('/train');
    } catch (error: any) {
      console.log('inside error', error);
      // Extract the error message
      let errorMessage = 'Failed to save workflow';
      errorMessage =
        error.response.data?.detail ||
        error.response.data?.message ||
        `Error: ${error.response.status}`;

      setValidationError(error.response.data?.detail || '');

      snack({
        message: errorMessage,
        severity: 'error'
      });
    } finally {
      setCreateLoad(false);
    }
  };

  function onBackClick() {
    if (method) {
      navigate('/train');
    } else {
      navigate('/train/activity_annotation');
    }
  }
  return (
    <div className="w-full p-4 flex flex-col gap-2">
      <div className="flex w-full items-center justify-between">
        <h2 className="text-[1.1vw] font-[Proxima-Nova-Bold]">Workflow</h2>
      </div>
      {flowtype === 'pick&place' ? (
        <WorkFlowConfig />
      ) : (
        <div>
          <Stack direction={'column'}>
            <Grid container py={3}>
              <DragAndDrop
                rows={rows?.data || []}
                activeId={activeId}
                setActiveId={setActiveId}
                addActivity={addActivity}
                setRows={setRows}
                handleChangeDependency={handleChangeDependency}
                selectedCameraID=""
                activities={activities}
                handleSort={handleSort}
              />
            </Grid>
          </Stack>

          {method ? (
            <Stack
              direction={'row'}
              justifyContent={'center'}
              spacing={3}
              mt={3}
            >
              <Button version="light" onClick={() => onBackClick()}>
                Back
              </Button>
              {method === 'edit' && (
                <>
                  <Button
                    loading={isLoading}
                    disabled={isLoading || !rows?.data?.length}
                    onClick={() => {
                      handleSave().then(() => {
                        snack({
                          message: 'Workflow saved successfully',
                          severity: 'success'
                        });
                        navigate('/train');
                      });
                    }}
                  >
                    Save
                  </Button>
                </>
              )}

              {method === 'new' && (
                <Button
                  loading={isLoading}
                  disabled={isLoading || !rows?.data?.length}
                  onClick={() => setSaveModal(true)}
                >
                  Save As
                </Button>
              )}
            </Stack>
          ) : (
            <Stack
              direction={'row'}
              justifyContent={'center'}
              spacing={3}
              mt={3}
            >
              {' '}
              <Button version="light" onClick={() => onBackClick()}>
                Back
              </Button>
              <Button
                loading={isLoading}
                disabled={isLoading || !rows?.data?.length}
                onClick={() => {
                  setModalOpen(true);
                }}
              >
                Train
              </Button>
            </Stack>
          )}
          <Modal
            open={isSaveModal}
            onClose={() => {
              setSaveModal(false);
            }}
            title={'Workflow Name'}
            size="sm"
          >
            <input
              type="text"
              onChange={e => handleInputChange(e.target.value)}
              style={{
                display: 'flex',
                justifyContent: 'center',
                border: '0.5px solid black',
                fontSize: '14px',
                padding: '4px',
                backgroundColor: 'transparent',
                width: '100%',
                marginBottom: '10px'
              }}
              placeholder="Please Enter Workflow Name"
            />
            {validationError && (
              <div className="text-red-500 text-xs mt-[-10px] w-full">
                {validationError}
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Button
                version="light"
                onClick={() => {
                  setSaveModal(false);
                  setValidationError('');
                }}
              >
                Cancel
              </Button>
              <Button
                loading={createLoad}
                onClick={() => {
                  handleSaveAs(workflowName);
                }}
                disabled={validationError.length ? true : false}
              >
                Create
              </Button>
            </div>
          </Modal>
          <Modal
            open={modalOpen}
            onClose={() => {
              setModalOpen(false);
              setValidationError('');
              setWorkFlowName('');
            }}
            title={
              !trainingStarted ? (
                <Typography
                  width={'100%'}
                  textAlign={'center'}
                  style={{ fontWeight: 'bold' }}
                >
                  Training Settings
                </Typography>
              ) : (
                ''
              )
            }
            size="md"
          >
            <TrainSummaryModal
              config_id={configId || ''}
              activities={activities}
              camera_length={cameras?.length || 0}
              workflow_data={rows?.data || []}
              onSumbit={handleTrain}
              loading={trainLoading || isLoading}
              trainingStarted={trainingStarted}
              videoList={videoListId}
              setWorkFlowName={setWorkFlowName}
              workflowName={workflowName}
            />
          </Modal>
        </div>
      )}
    </div>
  );
};

export default WorkflowMonitor;
